import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { find as _find, flatMap as _flatMap, get as _get } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { filter, first, map, tap, withLatestFrom } from 'rxjs/operators';

import { PolicySelectors as policiesQuery } from '@amfam/policy/data-access';
import {
  AutoPolicy,
  Discount,
  NamedInsured,
  PremiumEstimate,
  QuickQuoteBase,
  QuickQuoteEmail,
  QuickQuoteReplaceVehicleContext,
  QuickQuoteRequest,
  RiskModel,
  SubmitOnlineBindRequestModel,
  Vehicle
} from '@amfam/policy/models';
import { QuickQuoteDataFacade } from '@amfam/policy/replace-vehicle/data-access';
import {
  UnderwritingQuestion,
  VehicleFacade,
  VehicleFeaturesModel,
  VehicleSelectCriteria
} from '@amfam/policy/vehicle/data-access';
import { AnalyticsFacade, ReplaceVehicleAnalytics } from '@amfam/shared/analytics';
import { userQuery } from '@amfam/shared/user';
import { agentQuery } from '@amfam/shared/utility/agent';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import {
  ANSNotification,
  CopyService,
  ErrorMsg,
  InternalEmail,
  TemplatedEmail
} from '@amfam/shared/utility/shared-services';

import { QuickQuoteFeatureService } from '../../services/quick-quote-feature.service';
import { ReplaceVehicleQuickQuoteContext } from '../quick-quote-feature/quick-quote-enum';
import {
  AnalyticsEventType,
  ReplaceVehicleApiOperation,
  ReplaceVehicleQuickQuoteOperation
} from './quick-quote-enum';
import * as QuickQuoteFeatureActions from './quick-quote-feature.actions';
import { QuickQuoteFeatureState } from './quick-quote-feature.reducer';
import {
  getOperation,
  getQuoteToBind,
  getRemainingVehicles,
  getReplaceVehicleId,
  quickQuoteSelectors,
  selectContext
} from './quick-quote-feature.selectors';

@Injectable({ providedIn: 'root' })
export class QuickQuoteFeatureFacade {
  isVehicleLoading$: Observable<boolean> = this.vehicleFacade.isLoading$;
  vehicleYears$: Observable<number[]> = this.vehicleFacade.years$;
  vehicleMakes$: Observable<string[]> = this.vehicleFacade.makes$;
  vehicleModels$: Observable<string[]> = this.vehicleFacade.models$;
  vehicleSeries$: Observable<string[]> = this.vehicleFacade.series$;
  vehicleCriteria$: Observable<VehicleSelectCriteria> = this.vehicleFacade.criteria$;
  newRiskDescription$: Observable<string> = this.vehicleFacade.newRiskDescription$;
  quickQuote$: Observable<PremiumEstimate> = this.quickQuoteDataFacade.quickQuote$;
  workOrderNumber$: Observable<string> = this.quickQuoteDataFacade.workOrderNumber$;
  newVehicle$ = this.vehicleFacade.newVehicle$;
  vehicleToReplace$: Observable<RiskModel> = this.store.select(
    quickQuoteSelectors.getVehicleToReplace
  );
  existingVehicleRiskDescription$: Observable<string> = this.store.select(
    quickQuoteSelectors.getExistingVehicleRiskDescription
  );
  quickQuoteParams$: Observable<QuickQuoteRequest> = this.store.select(
    quickQuoteSelectors.getQuickQuoteParams
  );
  quickQuoteEmailInfo$: Observable<QuickQuoteEmail> = this.store.select(
    quickQuoteSelectors.getQuickQuoteEmailInfo
  );
  quickQuoteEmailInfoOnError$: Observable<QuickQuoteBase> = this.store.select(
    quickQuoteSelectors.getQuickQuoteBaseInfo
  );
  replaceVehicleId$: Observable<string> = this.store.select(
    quickQuoteSelectors.getReplaceVehicleId
  );
  policyNumberFromVehicle$: Observable<string> = this.store.select(
    quickQuoteSelectors.getPolicyNumberForVehicle
  );
  errorOnExceptionANS$: Observable<boolean> = this.quickQuoteDataFacade.errorOnExceptionANS$;
  errorOnANS$: Observable<boolean> = this.quickQuoteDataFacade.errorOnANS$;
  errorOnQuickQuote$ = this.quickQuoteDataFacade.errorOnQuickQuote$;

  agents$ = this.store.select(agentQuery.getRealAgents).pipe(
    withLatestFrom(this.store.select(policiesQuery.selectPolicies)),
    map(([agents, policies]) =>
      agents.filter(
        agent => agent && _find(policies, p => p.assignedProducer.producerIdNum === agent.id)
      )
    )
  );
  vehicleError$ = this.vehicleFacade.error$;
  vehicleStatus$ = this.vehicleFacade.status$;

  vinYearErrorMessage$ = combineLatest([
    this.vehicleFacade.error$,
    this.vehicleFacade.status$
  ]).pipe(
    map(([error, status]) => ({ error, status })),
    map(state => {
      if (state.error) {
        let errorMessage = this.copyService.get('policy', 'defaultReplaceVehicleErrorMessage');
        if (state.status) {
          this.copyService.get('policy', 'errorMsgs').some((errMsgObj: ErrorMsg) => {
            if (errMsgObj.key === _get(state, 'status.errors[0].message', '')) {
              errorMessage = errMsgObj.value;
              return true;
            }
          });
        }
        return errorMessage;
      }
    })
  );

  replaceVehicleRisks$ = this.store.select(quickQuoteSelectors.getReplaceVehicleRisks);
  advancedPolicies$ = this.store.select(policiesQuery.selectAdvanceAutoPolicies);
  userState$ = this.store.select(userQuery.selectUserState);
  vehiclePolicies$ = this.store.select(policiesQuery.selectVehiclePolicies);
  vehicleId$ = this.store.select(getReplaceVehicleId);
  context$ = this.store.select(selectContext);
  operation$ = this.store.select(getOperation);
  futurePayments$ = this.quickQuoteDataFacade.futurePayments$;
  quoteToBind$ = this.store.select(getQuoteToBind);
  remainingVehicles$ = this.store.select(getRemainingVehicles);

  quickQuoteBaseInfo$: Observable<QuickQuoteEmail> = this.store.select(
    quickQuoteSelectors.getQuickQuoteBaseInfo
  );
  getTpisInSelectedVehicle$: Observable<NamedInsured[]> = this.store.select(
    quickQuoteSelectors.getTpisInSelectedVehicle
  );
  getTpisInSelectedPolicy$: Observable<NamedInsured[]> = this.store.select(
    quickQuoteSelectors.getTpisInSelectedPolicy
  );
  selectedPolicy$: Observable<AutoPolicy> = this.store.select(
    quickQuoteSelectors.getSelectedPolicy
  );
  newVehicleDiscounts$: Observable<Discount[]> = this.store.select(
    quickQuoteSelectors.getNewVehicleDiscounts
  );

  selectedVehicle$: Observable<Vehicle> = this.store.select(quickQuoteSelectors.getSelectedVehicle);

  vehicleFeatures$ = this.vehicleFacade.vehicleFeatures$;

  constructor(
    private store: Store<QuickQuoteFeatureState>,
    private vehicleFacade: VehicleFacade,
    private quickQuoteDataFacade: QuickQuoteDataFacade,
    private quickQuoteService: QuickQuoteFeatureService,
    private copyService: CopyService,
    private analyticsFacade: AnalyticsFacade,
    private featureFlagService: FeatureFlagService
  ) {}

  lookupYearVin(year: string, vin: string) {
    this.vehicleFacade.addNewVehicle(year, vin);
    this.vehicleFacade.lookupVinYear(year, vin);
    this.sendAnalytics(AnalyticsEventType.BUTTON_REPLACE_VEHICLE_VIN_START_FIND_VIN);
  }

  loadVehicleMake(year: string) {
    this.vehicleFacade.addNewVehicle(year);
    this.vehicleFacade.loadMakes(year);
  }

  loadVehicleModels(year: string, make: string) {
    this.vehicleFacade.addNewVehicle(year, make);
    this.vehicleFacade.loadModels(year, make);
  }

  loadVehicleSeries(year: string, make: string, model: string) {
    this.vehicleFacade.addNewVehicle(year, make, model);
    this.vehicleFacade.loadSeries(year, make, model);
  }

  setVehicleId(vehicleId: string) {
    this.store.dispatch(QuickQuoteFeatureActions.setVehicleId({ vehicleId }));
  }

  setContext(context: ReplaceVehicleQuickQuoteContext) {
    this.store.dispatch(QuickQuoteFeatureActions.setContext({ context }));
  }

  setOperation(operation: ReplaceVehicleQuickQuoteOperation) {
    this.store.dispatch(QuickQuoteFeatureActions.setOperation({ operation }));
  }

  resetVehicle() {
    this.vehicleFacade.reset();
  }

  resetQuickQuoteFeature() {
    this.store.dispatch(QuickQuoteFeatureActions.resetQuickQuoteFeature());
  }

  resetQuickQuote() {
    this.resetVehicle();
    this.quickQuoteDataFacade.resetQuickQuote();
    this.resetQuickQuoteFeature();
  }

  addNewVehicle(year: string, make?: string, model?: string, series?: string) {
    this.vehicleFacade.addNewVehicle(year, make, model, series);
  }

  getQuickQuote() {
    this.quickQuoteParams$
      .pipe(
        first(),
        tap(params => this.quickQuoteDataFacade.getQuickQuote(params))
      )
      .subscribe();
    this.sendAnalytics(AnalyticsEventType.BUTTON_QUICK_QUOTE_NEXT);
  }

  getQuote(operation: QuickQuoteRequest['operation']) {
    if (
      operation === ReplaceVehicleApiOperation.REPLACE_VEHICLE &&
      this.featureFlagService.isEnabled('replace_vehicle_plpc_migration')
    ) {
      this.getQuotePLPC();
    } else {
      this.quickQuoteParams$
        .pipe(
          first(),
          tap(params => this.quickQuoteDataFacade.getQuickQuote({ ...params, operation }))
        )
        .subscribe();
    }

    this.sendAnalytics(AnalyticsEventType.REPLACE_VEHICLE_QUOTE_SPINNER);
  }

  getQuotePLPC() {
    this.quickQuoteParams$
      .pipe(
        first(),
        withLatestFrom(this.advancedPolicies$, this.vehicleFeatures$, this.newVehicle$),
        tap(([params, policies, vehicleFeatures, newVehicle]) => {
          const matchingPolicy = policies.find(
            policy => policy.policyNumber === params.policyNumber
          );

          this.quickQuoteDataFacade.getQuote({
            replaceVehicleVin: params.replaceVIN, //'JTDKAMFU2M3135606';
            policyNumber: params.policyNumber, //'410017889997';
            vehicle: {
              vin: params?.newVehicle?.vin, //'5N1AL0MM4DC301508';
              year: params?.newVehicle?.year, //'2013';
              make: newVehicle.make,
              model: newVehicle.model,
              series: newVehicle.series,

              annualMileage: 12000, //6000;
              primaryUsage: 'pleasure', //'pleasure';
              msrp: newVehicle.estimatedValue,

              garageAddress: {
                addressLine1: matchingPolicy?.policyAddress?.addressLine1, //'4145 Cherokee Dr';
                addressLine2: matchingPolicy?.policyAddress?.addressLine2,
                city: matchingPolicy?.policyAddress?.city, //'Brookfield';
                state: matchingPolicy?.policyAddress?.state, //'WI';
                postalCode: matchingPolicy?.policyAddress?.zipCode, //'53045';
                country: 'USA' //'USA';
              },

              airBagsIndicator: this.getAirBagsIndicator(vehicleFeatures), //string; //'Both';
              antiLockBrakes: this.getAntiLockBrakesIndicator(vehicleFeatures), // boolean; //true;
              antiTheftDeviceIndicator: this.getAntiTheftDeviceIndicator(vehicleFeatures), // string; //'none';
              dayTimeRunningLights: this.getDayTimeRunningLightsIndicator(vehicleFeatures), // boolean; //false;
              electronicStability: this.getDayTimeRunningLightsIndicator(vehicleFeatures), // boolean; //true;

              hasOtherOrCoowner: params.underwritingQuestions[0].answer === 'false' ? false : true, // boolean; //false;

              tpis: !!params?.newVehicle?.TPIName ? [params?.newVehicle?.TPIName] : []
            }
          });
        })
      )
      .subscribe();
  }

  getAirBagsIndicator(codeList: VehicleFeaturesModel[]) {
    const featureCodes = _flatMap(codeList, codeList => codeList.typeOfFeatureCode);
    for (let featureCode of featureCodes) {
      if (
        featureCode === this.copyService.getCopy('policy.newVehicleFeatureCodeFrontAndSideAirbags')
      ) {
        return 'Both';
      } else if (
        featureCode === this.copyService.getCopy('policy.newVehicleFeatureCodeFrontAirbags')
      ) {
        return 'front';
      } else if (
        featureCode === this.copyService.getCopy('policy.newVehicleFeatureCodeSideAirbags')
      ) {
        return 'other';
      }
    }
    return 'none';
  }

  getAntiLockBrakesIndicator(codeList: VehicleFeaturesModel[]) {
    const featureCodes = _flatMap(codeList, codeList => codeList.typeOfFeatureCode);
    for (let featureCode of featureCodes) {
      if (
        featureCode === this.copyService.getCopy('policy.newVehicleFeatureCodeAntiLockBrakes4w') ||
        featureCode === this.copyService.getCopy('policy.newVehicleFeatureCodeAntiLockBrakesRear')
      ) {
        return true;
      }
    }
    return false;
  }

  getAntiTheftDeviceIndicator(codeList: VehicleFeaturesModel[]) {
    const featureCodes = _flatMap(codeList, codeList => codeList.typeOfFeatureCode);
    for (let featureCode of featureCodes) {
      if (
        featureCode === this.copyService.getCopy('policy.newVehicleFeatureCodeAntiTheftPassive')
      ) {
        return 'passive';
      } else if (
        featureCode === this.copyService.getCopy('policy.newVehicleFeatureCodeAntiTheftActive')
      ) {
        return 'active';
      } else if (
        featureCode ===
        this.copyService.getCopy('policy.newVehicleFeatureCodeAntiTheftActivePassive')
      ) {
        return 'other';
      }
    }
    return 'none';
  }
  getDayTimeRunningLightsIndicator(codeList: VehicleFeaturesModel[]) {
    const featureCodes = _flatMap(codeList, codeList => codeList.typeOfFeatureCode);
    for (let featureCode of featureCodes) {
      if (
        featureCode === this.copyService.getCopy('policy.newVehicleFeatureCodeDaytimeRunningLights')
      ) {
        return true;
      }
    }
    return false;
  }
  getElectronicsStabilityIndicator(codeList: VehicleFeaturesModel[]) {
    const featureCodes = _flatMap(codeList, codeList => codeList.typeOfFeatureCode);
    for (let featureCode of featureCodes) {
      if (
        featureCode ===
        this.copyService.getCopy('policy.newVehicleFeatureCodeElectronicStabilityControl')
      ) {
        return true;
      }
    }
    return false;
  }

  sendToAgent() {
    this.quickQuoteEmailInfo$
      .pipe(
        first(),
        tap(email => {
          const sendToAgent = this.quickQuoteService.prepareSendToAgent(email);
          this.quickQuoteDataFacade.sendToAgent(
            sendToAgent.agentEmail,
            sendToAgent.customerEmail,
            sendToAgent.ansNotification
          );
        })
      )
      .subscribe();
    this.sendAnalytics(AnalyticsEventType.BUTTON_COMPLETE_SEND_TO_AGENT);
    this.analyticsFacade.trackEvent({
      event: 'agent_referral',
      form_name: 'Replace Vehicle'
    });
  }

  sendToAgentReplaceVehicle() {
    this.quickQuoteBaseInfo$
      .pipe(
        first(),
        tap(email => {
          const sendToAgent = this.quickQuoteService.prepareSendToAgent(
            email,
            false,
            QuickQuoteReplaceVehicleContext.EXPECTATION_SETTINGS
          );
          this.quickQuoteDataFacade.sendToAgent(
            sendToAgent.agentEmail,
            sendToAgent.customerEmail,
            sendToAgent.ansNotification
          );
        })
      )
      .subscribe();
  }

  sendToAgentOnError() {
    this.quickQuoteEmailInfoOnError$
      .pipe(
        first(),
        tap(email => {
          const sendToAgentOnError = this.quickQuoteService.prepareSendToAgent(email, true);
          this.quickQuoteDataFacade.sendToAgentOnError(
            sendToAgentOnError.agentEmail,
            sendToAgentOnError.customerEmail,
            sendToAgentOnError.ansNotification
          );
        })
      )
      .subscribe();
  }

  sendToAgentOnCancel(isCancelProcess = false) {
    this.quickQuoteBaseInfo$
      .pipe(
        withLatestFrom(
          this.replaceVehicleRisks$,
          this.replaceVehicleId$,
          this.vehicleCriteria$,
          this.workOrderNumber$
        ),
        first(),
        tap(([email, vehicles, vehicleId, criteria, workOrderNumber]) => {
          const matchingVehicle: RiskModel = vehicles.find(vehicle => vehicle.id === vehicleId);

          const sendToAgent = this.quickQuoteService.getSendToAgentInfo(
            {
              ...email,
              vehicle: matchingVehicle,
              tpiName: criteria?.TPIName || '',
              copyTpiIndicator: criteria?.copyTPIIndicator,
              hasTpi: this.quickQuoteService.hasTPI(criteria),
              orderNumber: workOrderNumber,
              isCancelProcess: isCancelProcess
            },
            this.copyService.getCopy('policy.replaceVehicleCancelProcess.customerEmail.templateId'),
            this.copyService.getCopy('policy.replaceVehicleCancelProcess.ANS.code')
          );
          this.quickQuoteDataFacade.sendToAgent(
            sendToAgent.agentEmail,
            sendToAgent.customerEmail,
            sendToAgent.ansNotification
          );
        })
      )
      .subscribe();
  }

  // sendAnalytics(analyticsPayload) {
  //   this.store.dispatch(QuickQuoteFeatureActions.sendAnalytics(analyticsPayload));
  // }

  setVehicleCriteria(criteriaOptions: VehicleSelectCriteria) {
    this.vehicleFacade.setCriteria(criteriaOptions);
  }

  setUnderWritingQuestions(underwritingQuestions: UnderwritingQuestion[]) {
    this.store.dispatch(
      QuickQuoteFeatureActions.setUnderWritingQuestions({ underwritingQuestions })
    );
  }

  sendAnalytics(
    eventType: AnalyticsEventType,
    agentName?: string,
    referenceNumber?: string,
    paymentFrequency = ''
  ) {
    switch (eventType) {
      case (eventType = AnalyticsEventType.QUOTE_OR_BIND):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pageQuoteOrPurchase,
          ReplaceVehicleAnalytics.eventQuoteOrPurchase
        );
        break;
      case (eventType = AnalyticsEventType.QUICK_QUOTE_START):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pageQuickQuoteStart,
          ReplaceVehicleAnalytics.eventQuickQuoteStart
        );
        break;
      case (eventType = AnalyticsEventType.QUICK_QUOTE_COMPLETE):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pageQuickQuoteComplete,
          ReplaceVehicleAnalytics.eventQuickQuoteComplete,
          null,
          {
            agentName: agentName,
            referenceNumber: referenceNumber
          }
        );
        break;
      case (eventType = AnalyticsEventType.QUICK_QUOTE_AGENT_SENT_TO_AGENT):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pageQuickQuoteSendToAgent,
          ReplaceVehicleAnalytics.eventQuickQuoteSendToAgent
        );
        break;
      case (eventType = AnalyticsEventType.QUICK_QUOTE_AGENT_HELP):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pageQuickQuoteAgentHelp,
          ReplaceVehicleAnalytics.eventQuickQuoteAgentHelp
        );
        break;
      case (eventType = AnalyticsEventType.QUICK_QUOTE_AGENT_NOTIFIED):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pageQuickQuoteAgentNotified,
          ReplaceVehicleAnalytics.eventQuickQuoteAgentNotified
        );
        break;
      case (eventType = AnalyticsEventType.BUTTON_QUICK_QUOTE_NEXT):
        this.analyticsFacade.trackButtonClick(ReplaceVehicleAnalytics.buttonQuickQuoteNext);
        this.analyticsFacade.trackButtonClick(ReplaceVehicleAnalytics.buttonQuickQuoteCalculating);
        break;
      case (eventType = AnalyticsEventType.BUTTON_COMPLETE_SEND_TO_AGENT):
        this.analyticsFacade.trackButtonClick(
          ReplaceVehicleAnalytics.buttonQuickQuoteCompleteSendToMyAgent
        );
        break;
      case (eventType = AnalyticsEventType.BUTTON_AGENT_HELP_SEND_TO_AGENT):
        this.analyticsFacade.trackButtonClick(
          ReplaceVehicleAnalytics.buttonQuickQuoteAgentHelpSendToMyAgent
        );
        break;
      case (eventType = AnalyticsEventType.BUTTON_AGENT_NOTIFIED_GET_ANOTHER_QUOTE):
        this.analyticsFacade.trackButtonClick(
          ReplaceVehicleAnalytics.buttonQuickQuoteAgentNotifiedGetAnotherQuote
        );
        break;
      case (eventType = AnalyticsEventType.BUTTON_COMPLETE_GET_ANOTHER_QUOTE):
        this.analyticsFacade.trackButtonClick(
          ReplaceVehicleAnalytics.buttonQuickQuoteCompleteGetAnotherQuote
        );
        break;

      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_VIN_YEAR):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pageReplaceVehicleVinYear,
          ReplaceVehicleAnalytics.eventReplaceVehicleVinYear
        );
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_UW_QUESTIONS):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pageReplaceVehicleUnderWritingQuestions
        );
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_SEND_TO_AGENT):
        this.analyticsFacade.trackButtonClick(
          ReplaceVehicleAnalytics.buttonReplaceVehicleSendToAgent
        );
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_BIND_COMPLETE):
        this.analyticsFacade.trackPageAndEvent(
          {
            ...ReplaceVehicleAnalytics.pageReplaceVehicleBindSuccess,
            ...{ subCategory2: paymentFrequency }
          },
          ReplaceVehicleAnalytics.eventReplaceVehicleBindSuccess,
          null,
          {
            agentName: agentName,
            referenceNumber: referenceNumber
          }
        );
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_SEND_TO_AGENT_CONFIRMATION):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pageReplaceVehicleSendToAgentSuccess,
          ReplaceVehicleAnalytics.eventReplaceVehicleSendToAgentSuccess,
          null,
          {
            agentName: agentName,
            referenceNumber: referenceNumber
          }
        );
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_HARD_STOP):
        this.analyticsFacade.trackPageAndEvent(ReplaceVehicleAnalytics.pageReplaceVehicleHardStop);
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_HARD_STOP_SEND_TO_AGENT):
        this.analyticsFacade.trackButtonClick(
          ReplaceVehicleAnalytics.buttonReplaceVehicleHardStopSendToAgent
        );
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_QUOTE_OR_BIND_FIND_VIN):
        this.analyticsFacade.trackButtonClick(
          ReplaceVehicleAnalytics.buttonReplaceVehicleQuoteOrPurchaseFindVIN
        );
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_QUOTE_OR_BIND_SEND_TO_AGENT):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pageReplaceVehicleQuoteOrPurchaseSentToAgent
        );
        break;
      case (eventType = AnalyticsEventType.BUTTON_REPLACE_VEHICLE_VIN_START_FIND_VIN):
        this.analyticsFacade.trackButtonClick(
          ReplaceVehicleAnalytics.buttonReplaceVehicleVINStartFindVIN
        );
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_FIND_VIN):
        this.analyticsFacade.trackPageAndEvent(ReplaceVehicleAnalytics.pageReplaceVehicleFindVIN);
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_QUOTE_SPINNER):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pageReplaceVehicleQuoteSpinner
        );
        break;

      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_QUOTE_SPINNER_NEED_AGENT):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pageReplaceVehicleQuoteSpinnerNeedAgent
        );
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_QUOTE_SPINNER_NEED_AGENT_CANCEL):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pageReplaceVehicleQuoteSpinnerNeedAgentCancel
        );
        break;

      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_QUOTE_SPINNER_NEED_AGENT_SENT_TO_AGENT):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pageReplaceVehicleQuoteSpinnerNeedAgentSendToAgent
        );
        break;

      case (eventType =
        AnalyticsEventType.REPLACE_VEHICLE_QUOTE_SPINNER_NEED_AGENT_CANCEL_SEND_TO_AGENT):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pageReplaceVehicleQuoteSpinnerNeedAgentCancelSendToAgent
        );
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_TPI_DETAILS):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pageReplaceVehicleTPIDetails
        );
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_TPI_DETAILS_CANCEL):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pageReplaceVehicleTPIDetailsCancel
        );
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_NEW_PREMIUM):
        this.analyticsFacade.trackPageAndEvent(ReplaceVehicleAnalytics.pageReplaceVehiclePremium);
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_NEW_PREMIUM_SEND_TO_AGENT):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pagePolicyReplaceVehicleNewPremiumSendToAgent
        );
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_NEW_PREMIUM_CANCEL):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pagePolicyReplaceVehicleNewPremiumCancel
        );
        break;

      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_NEW_PREMIUM_CANCEL_SEND_TO_AGENT):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pagePolicyReplaceVehicleNewPremiumCancelSendToAgent
        );
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_FUTURE_PAYMENTS):
        this.analyticsFacade.trackPageAndEvent({
          ...ReplaceVehicleAnalytics.pageReplaceVehicleFuturePayments,
          ...{ subCategory2: paymentFrequency }
        });
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_FUTURE_PAYMENTS_SEND_TO_AGENT):
        this.analyticsFacade.trackPageAndEvent({
          ...ReplaceVehicleAnalytics.pageReplaceVehicleFuturePaymentsSendToAgent,
          ...{ subCategory2: paymentFrequency }
        });
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_FUTURE_PAYMENTS_CANCEL):
        this.analyticsFacade.trackPageAndEvent({
          ...ReplaceVehicleAnalytics.pageReplaceVehicleFuturePaymentsCancel,
          ...{ subCategory2: paymentFrequency }
        });
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_FUTURE_PAYMENTS_CANCEL_SEND_TO_AGENT):
        this.analyticsFacade.trackPageAndEvent({
          ...ReplaceVehicleAnalytics.pageReplaceVehicleFuturePaymentsCancelSendToAgent,
          ...{ subCategory2: paymentFrequency }
        });
        break;

      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_FUTURE_PAYMENTS_NEED_AGENT):
        this.analyticsFacade.trackPageAndEvent({
          ...ReplaceVehicleAnalytics.pageReplaceVehicleFuturePaymentsNeedAgent,
          ...{ subCategory2: paymentFrequency }
        });
        break;
      case (eventType =
        AnalyticsEventType.REPLACE_VEHICLE_FUTURE_PAYMENTS_NEED_AGENT_SENT_TO_AGENT):
        this.analyticsFacade.trackPageAndEvent({
          ...ReplaceVehicleAnalytics.pageReplaceVehicleFuturePaymentNeedAgentSentToAgent,
          ...{ subCategory2: paymentFrequency }
        });
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_FUTURE_PAYMENTS_NEED_AGENT_CANCEL):
        this.analyticsFacade.trackPageAndEvent({
          ...ReplaceVehicleAnalytics.pageReplaceVehicleFuturePaymentsNeedAgentCancel,
          ...{ subCategory2: paymentFrequency }
        });
        break;
      case (eventType =
        AnalyticsEventType.REPLACE_VEHICLE_FUTURE_PAYMENTS_NEED_AGENT_CANCEL_SENT_TO_AGENT):
        this.analyticsFacade.trackPageAndEvent({
          ...ReplaceVehicleAnalytics.pageReplaceVehicleFuturePaymentsNeedAgentCancelSendToAgent,
          ...{ subCategory2: paymentFrequency }
        });
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_UW_DETAILS_CANCEL):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pageReplaceVehicleUWDetailsCancel
        );
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_UW_CANCEL_SEND_TO_AGENT):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pageReplaceVehicleUWDetailsCancelSendToAgent
        );
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_TPI_CANCEL_SEND_TO_AGENT):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pageReplaceVehicleTPIDetailsCancelSendToAgent
        );
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_ONLINE_BIND_ERROR):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pageReplaceVehicleOnlineBindError
        );
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_ONLINE_BIND_ERROR_SEND_TO_AGENT):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pageReplaceVehicleOnlineBindErrorSentToAgent
        );
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_HARD_STOP_OTHER_SEND_TO_AGENT):
        this.analyticsFacade.trackButtonClick(
          ReplaceVehicleAnalytics.buttonReplaceVehicleOtherSendToAgent
        );
        break;
      case (eventType = AnalyticsEventType.REPLACE_VEHICLE_HARD_STOP_OTHER_SEND_TO_AGENT_SUCCESS):
        this.analyticsFacade.trackPageAndEvent(
          ReplaceVehicleAnalytics.pageReplaceVehicleOtherSendToAgentSuccess
        );
        break;
    }
  }

  replaceVehicleNotifyAgent(emailPayload: InternalEmail) {
    this.quickQuoteDataFacade.sendAgentEmail(emailPayload);
  }

  replaceVehicleANSNotification(ansPayload: ANSNotification) {
    this.quickQuoteDataFacade.sendAnsNotification(ansPayload);
  }

  replaceVehicleNotifyCustomer(customerEmailPayload: TemplatedEmail) {
    this.quickQuoteDataFacade.sendCustomerEmailOnSendToAgent(customerEmailPayload);
  }

  bindQuoteOnline() {
    this.quoteToBind$
      .pipe(
        filter(q => !!q),
        tap(quoteToBind => {
          if (this.featureFlagService.isEnabled('replace_vehicle_plpc_migration')) {
            this.quickQuoteDataFacade.bindQuotePLPC(quoteToBind);
          } else {
            this.quickQuoteDataFacade.bindQuote(quoteToBind);
          }
        })
      )
      .subscribe();
  }

  setQuoteToBind(quoteToBind: SubmitOnlineBindRequestModel) {
    this.store.dispatch(QuickQuoteFeatureActions.setQuote({ payload: quoteToBind }));
  }

  loadFuturePayments() {
    this.policyNumberFromVehicle$
      .pipe(
        first(),
        tap(policyNumber => this.quickQuoteDataFacade.loadFuturePayments(policyNumber))
      )
      .subscribe();
  }
}
